import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import { List, ListItem, Stack } from "@mui/material";
import { InsuredTourist } from "./InsuredTourist";
import {
  every,
  isArray,
  isEqual,
  isString,
  last,
  pick,
  size,
  some,
  trim,
} from "lodash";
import { FormProvider, useFormContext, useWatch } from "react-hook-form";
import { Button } from "../../Button";
import { WebShopIcon } from "../../WebShopIcon";
import {
  ShortTouristFieldType,
  touristFieldDefaultObject,
  TouristFieldType,
  touristValidatingSchema,
} from "@/src/shared/schemas";
import useToggle from "@/src/hooks/useToggle";
import dynamic from "next/dynamic";
import {
  INSURED_COUNT_VAR_LIST,
  INSURED_COUNT_VAR_LIST_VISA_CENTRE,
  INSURED_TOURISTS_CONST,
} from "../../ProposalTable/lib/const";
import { v4 as uuidv4 } from "uuid";
import { useThrottledCallback } from "use-debounce";
import { useUpdateField } from "@/src/hooks/useFieldDebouncedCallback";
import { actionRunningStore } from "@/src/hooks/blockly/useHandleBlocklyFields";
import { useStore } from "zustand";
import { recalcInsuredCounts, recalcInsuredCountsVisaCentre } from "@/src/components/Action/useFormDataMiddleware";
import { getAgeFromDate } from "@/src/shared/utils/dateTime";
import {useAnalytics} from "@/src/components/pages/StepPage/components/useAnalytics";
import { FlowCodes } from "@/src/components/pages/StepPage/components/apiCalls/contants";

// Visa Centre prices on 3 collapsed age bands instead of Tourism's 5 — recalcInsuredCounts()
// (Tourism's shape) never writes insuredCountAgeFrom0To64, so it silently stayed unset in
// context, which TravelPremiumIntegrityValidator's age-distribution check then flagged as a
// mismatch against the freshly-derived buckets, blocking billing (premiumIntegrityMismatch).
const INSURED_COUNT_RECALC_CONFIG: Partial<
  Record<string, { recalc: (tourists: any[]) => Record<string, number>; bucketKeys: string[] }>
> = {
  [FlowCodes.VZK_VISA_CENTER]: {
    recalc: recalcInsuredCountsVisaCentre,
    bucketKeys: INSURED_COUNT_VAR_LIST_VISA_CENTRE,
  },
};

const UnableAddUserModal = dynamic(() => import("./UnableAddUserModal"));

interface InsuredTouristsListProps {
  name: string;
  maxTourists?: number;
  hideAddTouristButton?: boolean;
  style?: React.CSSProperties;
}

export const InsuredTouristsList: FC<InsuredTouristsListProps & any> = ({
  name,
  maxTourists = 10,
  hideAddTouristButton = false,
  tourist_lastNameProps,
  tourist_firstNameProps,
  tourist_lastNameLatinProps,
  tourist_firstNameLatinProps,
  tourist_citizenshipProps,
  tourist_foreignPassportNumberProps,
  tourist_identCodeProps,
  tourist_birthDateProps,
  tourist_cityProps,
  tourist_streetProps,
  tourist_streetTypeProps,
  tourist_houseNumberProps,
  tourist_flatNumberProps,
  style,
}) => {
  const touristsListFieldName = name ?? "";
  const { trackAnalyticEvent } = useAnalytics();

  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(false);
  const {
    isToggled: isOpenedUnableAddUserModal,
    open: openUnableAddUserModal,
    close: closeUnableAddUser,
  } = useToggle({ initialState: false });

  const updateField = useUpdateField();
  const formMethods = useFormContext();
  const { control, setValue, getValues } = formMethods;

  const touristFields = useWatch({ control, name: touristsListFieldName });
  const prevCountsRef = useRef<any>();

  const append = useCallback(
    (item: any) => {
      const touristsList = getValues(touristsListFieldName);

      if (isArray(touristsList)) {
        const newItem = { ...item, id: uuidv4() };
        const mappedTouristsList = [...touristsList, newItem].map(
          (item, idx, self) => ({
            ...item,
            isExpanded: size(self) - 1 === idx,
          }),
        );
        setValue(touristsListFieldName, mappedTouristsList);
      }
    },
    [getValues, setValue],
  );

  const remove = useCallback(
    async (index: number) => {
      const touristsList = getValues(touristsListFieldName)?.filter(
        (_: any, idx: any) => idx !== index,
      );
      setValue(touristsListFieldName, touristsList);
      await updateField(touristsListFieldName, touristsList);
    },
    [getValues, setValue],
  );

  const replace = useCallback(
    (tourists: any[]) => {
      setValue(
        touristsListFieldName,
        tourists?.map((item) => ({ ...item, id: uuidv4() })),
      );
    },
    [setValue],
  );

  const watchedFields = useWatch({
    control,
    name: [
      "isInsurerTourist",
      "lastName",
      "firstName",
      "lastNameLatin",
      "firstNameLatin",
      "citizenship",
      "foreignPassportNumber",
      "identCode",
      "birthDate",
      "street",
      "houseNumber",
      "flatNumber",
      "city",
      "streetType",
    ],
  });

  const [
    isInsurerTourist,
    tourist_lastName,
    tourist_firstName,
    tourist_lastNameLatin,
    tourist_firstNameLatin,
    tourist_citizenship,
    tourist_foreignPassportNumber,
    tourist_identCode,
    tourist_birthDate,
    tourist_street,
    tourist_houseNumber,
    tourist_flatNumber,
    tourist_city,
    tourist_streetType,
  ] = watchedFields;

  const insurerTouristObj = useMemo(() => {
    return {
      tourist_lastName,
      tourist_firstName,
      tourist_lastNameLatin,
      tourist_firstNameLatin,
      tourist_citizenship,
      tourist_foreignPassportNumber,
      tourist_identCode,
      tourist_birthDate,
      tourist_street,
      tourist_houseNumber,
      tourist_flatNumber,
      tourist_city,
      tourist_streetType,
      isSameAddress: false,
      isInsurerTourist: true,
      showDeleteButton: false,
      showSameAddressCheckbox: false,
      readOnly: true,
      isExpanded: false,
    };
  }, [
    tourist_lastName,
    tourist_firstName,
    tourist_lastNameLatin,
    tourist_firstNameLatin,
    tourist_citizenship,
    tourist_foreignPassportNumber,
    tourist_identCode,
    tourist_birthDate,
    tourist_street,
    tourist_houseNumber,
    tourist_flatNumber,
    tourist_city,
    tourist_streetType,
  ]);

  const getTouristFieldsKeysList = useCallback((lastTourist: any) => {
    const touristFieldsKeysList = touristValidatingSchema.keyof().options;

    const userAge = getAgeFromDate(lastTourist?.tourist_birthDate) ?? Infinity;
    if (userAge >= 18) {
      // @ts-ignore
      touristFieldsKeysList.push("tourist_identCode");
    }
    return touristFieldsKeysList;
  }, []);

  const hasEmptyFields = (field: TouristFieldType) => some(field, (fieldValue) => {
    return isString(fieldValue) ? !trim(fieldValue) : false;
  })

  const transformToShorTourists = (field: TouristFieldType): ShortTouristFieldType => ({
    tourist_firstName: field.tourist_firstName,
    tourist_lastName: field.tourist_lastName,
    tourist_identCode: field.tourist_identCode,
    tourist_street: field.tourist_street,
    tourist_foreignPassportNumber: field.tourist_foreignPassportNumber,
    tourist_birthDate: field.tourist_birthDate,
    tourist_houseNumber: field.tourist_houseNumber
  });

  const isEmptyFields = (field: ShortTouristFieldType) => every(field, (fieldValue) => {
    return isString(fieldValue) ? !trim(fieldValue) : false;
  })

  const throttledisAddButtonDisabled = useThrottledCallback((touristFields) => {
    const lastTourist = last(touristFields) as any;
    if (!lastTourist || lastTourist.isInsurerTourist) {
      setIsAddButtonDisabled(false);
      return;
    }
    const touristFieldsKeysList = getTouristFieldsKeysList(lastTourist);

    const touristFilteredFields = pick(lastTourist, touristFieldsKeysList);

    const hasEmptyField = some(touristFilteredFields, (fieldValue) => {
      return isString(fieldValue) ? !trim(fieldValue) : !fieldValue;
    });
    setIsAddButtonDisabled(!!hasEmptyField);
  }, 800);

  useEffect(() => {
    if (!isArray(touristFields)) return;

    throttledisAddButtonDisabled(touristFields);
  }, [touristFields]);

  const isActionRunning = useStore(
    actionRunningStore,
    ({ isActionRunning }) => isActionRunning,
  );

  useEffect(() => {
    const currentTourists = getValues(touristsListFieldName);
    console.log("currentTourists", currentTourists);
    if (
      !isArray(currentTourists) ||
      actionRunningStore.getState().isActionRunning
    )
      return;

    const hasInsurer = currentTourists.some(
      (t: TouristFieldType) => t.isInsurerTourist,
    );

    if (isInsurerTourist && !hasInsurer) {
      const filtered = currentTourists
        .filter((t: TouristFieldType) => !t.isInsurerTourist)
        .filter((t) => !isEmptyFields(transformToShorTourists(t)))
        .map((tourist: TouristFieldType) => ({
          ...tourist,
          readOnly: false,
          showDeleteButton: true,
          showSameAddressCheckbox: true,
          isExpanded: hasEmptyFields(tourist),
          ...(tourist?.isSameAddress
            ? {
                tourist_street: insurerTouristObj.tourist_street,
                tourist_flatNumber: insurerTouristObj.tourist_flatNumber,
                tourist_houseNumber: insurerTouristObj.tourist_houseNumber,
                tourist_city: insurerTouristObj.tourist_city,
                tourist_streetType: insurerTouristObj.tourist_streetType,
                isSameAddress: true,
              }
            : {
                tourist_street: tourist.tourist_street,
                tourist_flatNumber: tourist.tourist_flatNumber,
                tourist_houseNumber: tourist.tourist_houseNumber,
                tourist_city: tourist.tourist_city,
                tourist_streetType: tourist.tourist_streetType,
                isSameAddress: false,
              }),
        }))
        // Insurer takes one of the maxTourists slots — drop already-filled tourists that
        // no longer fit instead of exceeding the cap.
        .slice(0, Math.max(0, maxTourists - 1));

      replace([insurerTouristObj, ...filtered]);
    }

    if (!isInsurerTourist && hasInsurer) {
      const filtered = currentTourists
        .filter((t: TouristFieldType) => !t.isInsurerTourist)
        .map((tourist: TouristFieldType, i) => ({
          ...tourist,
          readOnly: false,
          showDeleteButton: true,
          showSameAddressCheckbox: true,
          isExpanded: hasEmptyFields(tourist),
          ...(tourist?.isSameAddress
            ? {
                tourist_street: insurerTouristObj.tourist_street,
                tourist_flatNumber: insurerTouristObj.tourist_flatNumber,
                tourist_houseNumber: insurerTouristObj.tourist_houseNumber,
                tourist_city: insurerTouristObj.tourist_city,
                tourist_streetType: insurerTouristObj.tourist_streetType,
                isSameAddress: true,
              }
            : {
                tourist_street: tourist.tourist_street,
                tourist_flatNumber: tourist.tourist_flatNumber,
                tourist_houseNumber: tourist.tourist_houseNumber,
                tourist_city: tourist.tourist_city,
                tourist_streetType: tourist.tourist_streetType,
                isSameAddress: false,
              }),
        }));

      if (filtered?.length === 0 && !isInsurerTourist) {
        replace([{
          ...touristFieldDefaultObject,
          isExpanded: true,
          isSameAddress: false,
          isInsurerTourist: false,
          readOnly: false,
          showDeleteButton: true,
          showSameAddressCheckbox: true,
        }]);
      } else {
        replace(filtered);
      }


    }

    if (!isInsurerTourist && !hasInsurer && size(currentTourists) === 0) {
      console.log("touristFieldDefaultObject", touristFieldDefaultObject);
      replace([
        {
          ...touristFieldDefaultObject,
          isExpanded: true,
          isSameAddress: false,
          isInsurerTourist: false,
          readOnly: false,
          showDeleteButton: true,
          showSameAddressCheckbox: true,
        },
      ]);
    }
  }, [
    isInsurerTourist,
    insurerTouristObj,
    touristsListFieldName,
    isActionRunning,
    maxTourists,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    const flowCode = getValues("flowCode");
    const recalcConfig = INSURED_COUNT_RECALC_CONFIG[flowCode];
    const recalcFn = recalcConfig?.recalc ?? recalcInsuredCounts;
    const bucketKeys = recalcConfig?.bucketKeys ?? INSURED_COUNT_VAR_LIST;

    const newCounts = recalcFn(touristFields);

    if (prevCountsRef.current && isEqual(newCounts, prevCountsRef.current)) {
      return;
    }

    prevCountsRef.current = newCounts;
    setValue("insuredTourists", newCounts);
    bucketKeys.forEach((key) => setValue(key, (newCounts as Record<string, number>)[key]));

    updateField(INSURED_TOURISTS_CONST, newCounts);
  }, [touristFields]);

  const updateTouristsList = useThrottledCallback(
      useCallback((touristsListFieldName, touristFields) => {
        updateField(touristsListFieldName, touristFields);
      }, []),
      1000,
  );

  useEffect(() => {
    updateTouristsList(touristsListFieldName, touristFields);

  }, [touristFields]);

  const handleAddressCheckboxChange = (
    touristIndex: number,
    isSameAddress: boolean,
  ) => {
    if (isSameAddress) {
      setValue(
        `${touristsListFieldName}.${touristIndex}.tourist_street`,
        insurerTouristObj?.tourist_street,
      );
      setValue(
        `${touristsListFieldName}.${touristIndex}.tourist_houseNumber`,
        insurerTouristObj?.tourist_houseNumber,
      );
      setValue(
        `${touristsListFieldName}.${touristIndex}.tourist_flatNumber`,
        insurerTouristObj?.tourist_flatNumber,
      );
      setValue(
        `${touristsListFieldName}.${touristIndex}.tourist_city`,
        insurerTouristObj?.tourist_city,
      );
      setValue(
        `${touristsListFieldName}.${touristIndex}.tourist_streetType`,
        insurerTouristObj?.tourist_streetType,
      );
    } else {
      setValue(`${touristsListFieldName}.${touristIndex}.tourist_street`, "");
      setValue(
        `${touristsListFieldName}.${touristIndex}.tourist_houseNumber`,
        "",
      );
      setValue(
        `${touristsListFieldName}.${touristIndex}.tourist_flatNumber`,
        "",
      );
      setValue(`${touristsListFieldName}.${touristIndex}.tourist_city`, null);
      setValue(
        `${touristsListFieldName}.${touristIndex}.tourist_streetType`,
        null,
      );
    }
  };

  const handleAddTourist = () => {
    trackAnalyticEvent("add_insured")
    if (size(touristFields) >= maxTourists) {
      openUnableAddUserModal();
      return;
    }
    append({
      ...touristFieldDefaultObject,
      isSameAddress: false,
      isInsurerTourist: false,
      readOnly: false,
      showDeleteButton: true,
      showSameAddressCheckbox: true,
      id: uuidv4(),
    });
  };

  const onToggleExpansion = (targetIndex: number) => {
    const touristsList = getValues(touristsListFieldName);

    if (isArray(touristsList)) {
      const mappedTouristsList = touristsList.map((tourist, index) => ({
        ...tourist,
        isExpanded:
          index === targetIndex ? !tourist.isExpanded : !!tourist.isExpanded,
      }));
      setValue(touristsListFieldName, mappedTouristsList);
    }
  };

  const handleDeleteTourist = (index: number) => {
    trackAnalyticEvent("delete_insured")
    remove(index)
  };

  const closeUnableAddUserModal = () => {
    trackAnalyticEvent("delete_canceled")
    closeUnableAddUser();
  };

  const isVisaCentre = getValues("flowCode") === FlowCodes.VZK_VISA_CENTER;

  return (
    <FormProvider {...formMethods}>
      <Stack className="flex w-full flex-col gap-4">
        <List className="flex flex-col gap-4">
          {touristFields?.map((tourist: any, index: number) => (
            // <ListItem key={tourist.id ?? index}>
            <InsuredTourist
              key={tourist.id ?? index}
              heading={isVisaCentre ? "Застрахований" : `Турист ${index + 1}`}
              handleDelete={() => {
                trackAnalyticEvent("delete_confirmed")
                handleDeleteTourist(index)
              }}
              style={style}
              onAddressCheckboxChange={handleAddressCheckboxChange}
              onToggleExpansion={onToggleExpansion}
              showSameAddressCheckbox={!!tourist.showSameAddressCheckbox}
              showDeleteButton={
                (!!tourist.showDeleteButton || !tourist.isInsurerTourist) && index !== 0
              }
              readOnly={!!tourist.readOnly}
              isContentExpanded={!!tourist?.isExpanded}
              index={index}
              touristsListItemFieldName={`${touristsListFieldName}.${index}`}
              tourist_lastNameProps={tourist_lastNameProps}
              tourist_firstNameProps={tourist_firstNameProps}
              tourist_lastNameLatinProps={tourist_lastNameLatinProps}
              tourist_firstNameLatinProps={tourist_firstNameLatinProps}
              tourist_citizenshipProps={tourist_citizenshipProps}
              tourist_foreignPassportNumberProps={
                tourist_foreignPassportNumberProps
              }
              tourist_identCodeProps={tourist_identCodeProps}
              tourist_birthDateProps={tourist_birthDateProps}
              tourist_cityProps={tourist_cityProps}
              tourist_streetProps={tourist_streetProps}
              tourist_streetTypeProps={tourist_streetTypeProps}
              tourist_houseNumberProps={tourist_houseNumberProps}
              tourist_flatNumberProps={tourist_flatNumberProps}
            />
            // </ListItem>
          ))}
        </List>

        {!hideAddTouristButton && (
          <Button
            startIcon={<WebShopIcon name="plus" />}
            variant="text"
            fullWidth={false}
            onClick={handleAddTourist}
            className="justify-start"
            disabled={isAddButtonDisabled}
          >
            додати туриста (Застрахованого)
          </Button>
        )}
      </Stack>

      {isOpenedUnableAddUserModal && (
        <UnableAddUserModal
          isOpen={isOpenedUnableAddUserModal}
          onCancel={closeUnableAddUserModal}
          onClose={closeUnableAddUserModal}
          onAccept={closeUnableAddUserModal}
          maxTourists={maxTourists}
        />
      )}
    </FormProvider>
  );
};

InsuredTouristsList.displayName = "InsuredTouristsList";
